import { useState, useEffect } from 'react';

const NAV_LINKS = [
  { label: 'About', href: '#about' },
  { label: 'Why Inclusive', href: '#why' },
  { label: 'How It Works', href: '#how' },
  { label: 'Robot', href: '#robot' },
  { label: 'Offers', href: '#offers' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);

      const sections = NAV_LINKS.map((l) => l.href.replace('#', ''));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && window.scrollY >= el.offsetTop - 100) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAnchor = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-gray-100'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a href="#hero" onClick={(e) => handleAnchor(e, '#hero')} className="flex-shrink-0">
            <img
              src="https://kids.nanitrobot.com/wp-content/themes/nanit/assets/images/logo.svg"
              alt="Nanit Inclusive"
              className={`h-8 md:h-9 transition-all duration-300 ${scrolled ? '' : 'brightness-0 invert'}`}
            />
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const id = link.href.replace('#', '');
              const isActive = activeSection === id;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchor(e, link.href)}
                  className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer
                    ${isActive
                      ? 'text-yellow-500'
                      : scrolled
                        ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-yellow-400"></span>
                  )}
                </a>
              );
            })}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="#booking"
              onClick={(e) => handleAnchor(e, '#booking')}
              className={`text-sm font-semibold px-4 py-2 rounded-full border-2 transition-all duration-200 whitespace-nowrap cursor-pointer
                ${scrolled
                  ? 'border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-gray-900'
                }`}
            >
              Book a Meeting
            </a>
            <a
              href="#contact"
              onClick={(e) => handleAnchor(e, '#contact')}
              className="text-sm font-semibold px-4 py-2 rounded-full bg-yellow-400 text-gray-900 hover:bg-yellow-300 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Order Now
            </a>
          </div>

          {/* Hamburger */}
          <button
            className="lg:hidden flex flex-col gap-1.5 p-2 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''} ${scrolled ? 'bg-gray-800' : 'bg-white'}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''} ${scrolled ? 'bg-gray-800' : 'bg-white'}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''} ${scrolled ? 'bg-gray-800' : 'bg-white'}`}></span>
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)}></div>
        <div className={`absolute top-0 right-0 h-full w-72 bg-white shadow-2xl transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <img src="https://kids.nanitrobot.com/wp-content/themes/nanit/assets/images/logo.svg" alt="Nanit" className="h-8" />
            <button onClick={() => setMobileOpen(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-close-line text-xl text-gray-700"></i>
            </button>
          </div>
          <nav className="flex flex-col px-4 py-6 gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchor(e, link.href)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-yellow-50 rounded-xl transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <div className="mt-4 flex flex-col gap-3 px-4">
              <a
                href="#booking"
                onClick={(e) => handleAnchor(e, '#booking')}
                className="text-center font-semibold py-3 rounded-full border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Book a Meeting
              </a>
              <a
                href="#contact"
                onClick={(e) => handleAnchor(e, '#contact')}
                className="text-center font-semibold py-3 rounded-full bg-yellow-400 text-gray-900 hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                Order Now
              </a>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
